import React, { useState } from "react";
import CustomModalV1 from "components/custom-modal/CustomModalV1";
import Button from "components/button/Button";
import { XIconV1 } from "images/image-component";
import { ColumnCounterpartyFormData } from "types/column";
import {
  normalizeRoutingNumber,
  routingNumberError,
  routingNumberSubmitError,
} from "utils/routingNumber";
import "./column-counterparty-modal.css";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: ColumnCounterpartyFormData) => void;
  submitting?: boolean;
}

function ColumnCounterpartyModal({
  isOpen,
  onClose,
  onSubmit,
  submitting = false,
}: Props) {
  const [name, setName] = useState("");
  const [routingNumber, setRoutingNumber] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [confirmAccount, setConfirmAccount] = useState("");
  const [accountType, setAccountType] = useState<"checking" | "savings">(
    "checking",
  );
  const [legalId, setLegalId] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [error, setError] = useState("");

  // Inline, as the merchant types. Stays null until 9 digits are entered so a half-typed number
  // is not reported as wrong.
  const routingFieldError = routingNumberError(routingNumber);
  const confirmAccountError =
    confirmAccount.length > 0 && accountNumber !== confirmAccount
      ? "Account numbers do not match."
      : null;

  function handleSubmit() {
    if (
      !name.trim() ||
      !routingNumber.trim() ||
      !accountNumber.trim() ||
      !addressLine1.trim() ||
      !city.trim()
    ) {
      setError("Please fill in all required fields.");
      return;
    }
    const routingProblem = routingNumberSubmitError(routingNumber);
    if (routingProblem) {
      setError(routingProblem);
      return;
    }
    if (accountNumber !== confirmAccount) {
      setError("Account numbers do not match.");
      return;
    }
    setError("");
    onSubmit({
      name: name.trim(),
      routingNumber,
      accountNumber,
      accountType,
      legalId: legalId.trim(),
      addressLine1: addressLine1.trim(),
      city: city.trim(),
      state: state.trim(),
      postalCode: postalCode.trim(),
    });
  }

  return (
    <CustomModalV1 isOpen={isOpen} handleClose={onClose}>
      <button className="v1-modal-close" onClick={onClose} aria-label="Close">
        <XIconV1 />
      </button>

      <form
        className="ccp-modal"
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <h3 className="ccp-modal__title">Add your bank account</h3>
        <p className="ccp-modal__desc">
          We pay out directly to this US bank account via Column. You only need
          to do this once.
        </p>

        <div className="ccp-modal__scroll">
          <div className="ccp-modal__field">
            <label className="ccp-modal__label" htmlFor="ccp-name">
              Account holder name
            </label>
            <input
              id="ccp-name"
              className="ccp-modal__input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Acme Coffee LLC"
            />
          </div>

          <div className="ccp-modal__field">
            <label className="ccp-modal__label" htmlFor="ccp-routing">
              Routing number (ABA)
            </label>
            <input
              id="ccp-routing"
              className="ccp-modal__input ccp-modal__input--mono"
              inputMode="numeric"
              maxLength={9}
              value={routingNumber}
              onChange={(e) =>
                setRoutingNumber(normalizeRoutingNumber(e.target.value))
              }
              placeholder="021000021"
              aria-invalid={routingFieldError ? true : undefined}
              aria-describedby={
                routingFieldError ? "ccp-routing-error" : undefined
              }
            />
            {routingFieldError && (
              <p className="ccp-modal__field-error" id="ccp-routing-error">
                {routingFieldError}
              </p>
            )}
          </div>

          <div className="ccp-modal__row">
            <div className="ccp-modal__field">
              <label className="ccp-modal__label" htmlFor="ccp-account">
                Account number
              </label>
              <input
                id="ccp-account"
                className="ccp-modal__input ccp-modal__input--mono"
                value={accountNumber}
                onChange={(e) =>
                  setAccountNumber(e.target.value.replace(/\s+/g, ""))
                }
              />
            </div>
            <div className="ccp-modal__field">
              <label className="ccp-modal__label" htmlFor="ccp-account-confirm">
                Confirm
              </label>
              <input
                id="ccp-account-confirm"
                className="ccp-modal__input ccp-modal__input--mono"
                value={confirmAccount}
                onChange={(e) =>
                  setConfirmAccount(e.target.value.replace(/\s+/g, ""))
                }
                aria-invalid={confirmAccountError ? true : undefined}
                aria-describedby={
                  confirmAccountError ? "ccp-confirm-error" : undefined
                }
              />
              {confirmAccountError && (
                <p className="ccp-modal__field-error" id="ccp-confirm-error">
                  {confirmAccountError}
                </p>
              )}
            </div>
          </div>

          <div className="ccp-modal__field">
            <label className="ccp-modal__label" htmlFor="ccp-type">
              Account type
            </label>
            <select
              id="ccp-type"
              className="ccp-modal__select"
              value={accountType}
              onChange={(e) =>
                setAccountType(e.target.value as "checking" | "savings")
              }
            >
              <option value="checking">Checking</option>
              <option value="savings">Savings</option>
            </select>
          </div>

          <div className="ccp-modal__field">
            <label className="ccp-modal__label" htmlFor="ccp-legal">
              Tax ID — EIN / SSN{" "}
              <span className="ccp-modal__opt">(optional)</span>
            </label>
            <input
              id="ccp-legal"
              className="ccp-modal__input ccp-modal__input--mono"
              value={legalId}
              onChange={(e) => setLegalId(e.target.value)}
              placeholder="12-3456789"
            />
          </div>

          <div className="ccp-modal__field">
            <label className="ccp-modal__label" htmlFor="ccp-address">
              Billing address
            </label>
            <input
              id="ccp-address"
              className="ccp-modal__input"
              value={addressLine1}
              onChange={(e) => setAddressLine1(e.target.value)}
              placeholder="1 Market Street"
            />
          </div>

          <div className="ccp-modal__row ccp-modal__row--3">
            <input
              aria-label="City"
              className="ccp-modal__input"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="City"
            />
            <input
              aria-label="State"
              className="ccp-modal__input"
              value={state}
              onChange={(e) => setState(e.target.value)}
              placeholder="State"
            />
            <input
              aria-label="ZIP"
              className="ccp-modal__input ccp-modal__input--mono"
              value={postalCode}
              onChange={(e) => setPostalCode(e.target.value)}
              placeholder="ZIP"
            />
          </div>
        </div>

        {error && (
          <p className="ccp-modal__error" role="alert">
            {error}
          </p>
        )}

        <div className="ccp-modal__actions">
          <Button
            variant="ghost"
            type="button"
            onClick={onClose}
            disabled={submitting}
          >
            Back
          </Button>
          <Button variant="primary" type="submit" disabled={submitting}>
            {submitting ? "Saving…" : "Save bank account"}
          </Button>
        </div>
      </form>
    </CustomModalV1>
  );
}

export default ColumnCounterpartyModal;
